import { LEARNING_PLAN_ACTIVITY_SEED } from '../data/learningPlanActivitySeed';
import {
  LEARNING_PLAN_BUILTIN_CATEGORIES,
  isLearningPlanBuiltinCategory,
  type LearningPlanActivity,
  type LearningPlanCategory,
} from '../types/learningPlanActivity';

const STORAGE_KEY = 'cp-learning-plan-activities-v1';
const CATEGORY_KEY = 'cp-learning-plan-categories-v1';
const ID_RE = /^[A-Za-z0-9_-]{1,40}$/;

function isActivity(value: unknown): value is LearningPlanActivity {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<LearningPlanActivity>;
  return (
    typeof row.id === 'string' &&
    typeof row.phase === 'string' &&
    ID_RE.test(row.phase) &&
    typeof row.name === 'string' &&
    typeof row.method === 'string' &&
    typeof row.guide === 'string' &&
    typeof row.sort === 'number'
  );
}

function isCategory(value: unknown): value is LearningPlanCategory {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<LearningPlanCategory>;
  return typeof row.id === 'string' && ID_RE.test(row.id) && typeof row.name === 'string' && typeof row.sort === 'number';
}

function defaultCategories(): LearningPlanCategory[] {
  return LEARNING_PLAN_BUILTIN_CATEGORIES.map((row) => ({ ...row }));
}

/** 저장된 카테고리가 없으면 도입활동·배움활동·정리활동. 기본 세 칸은 항상 남긴다. */
export function loadLearningPlanCategories(): LearningPlanCategory[] {
  try {
    const raw = window.localStorage.getItem(CATEGORY_KEY);
    if (raw == null) return defaultCategories();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultCategories();
    const saved = parsed.filter(isCategory);
    const builtins = LEARNING_PLAN_BUILTIN_CATEGORIES.map((base) => {
      const hit = saved.find((row) => row.id === base.id);
      const name = hit?.name.trim() || base.name;
      return { id: base.id, name, sort: base.sort };
    });
    const custom = saved
      .filter((row) => !isLearningPlanBuiltinCategory(row.id))
      .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'ko'));
    return [...builtins, ...custom];
  } catch {
    return defaultCategories();
  }
}

export function saveLearningPlanCategories(rows: LearningPlanCategory[]): void {
  window.localStorage.setItem(CATEGORY_KEY, JSON.stringify(rows));
}

/** 안내를 절차형으로 바꾸기 전 기본 문장. 이 문장 그대로인 기본 활동만 새 안내로 바꾼다. */
const PREVIOUS_SEED_GUIDES: Record<string, string> = {
  'intro-story': '학습내용과 닿은 짧은 이야기·일화로 궁금증을 엽니다.',
  'intro-material': '사진, 실물, 영상처럼 볼 자료를 짧게 보여주고 관찰 포인트를 묻습니다.',
  'intro-question': '답이 하나로 닫히지 않는 질문으로 생각을 꺼냅니다.',
  'intro-experience': '생활 속 경험을 학습내용과 잇습니다.',
  'intro-prior': '이미 아는 것과 궁금한 것을 짧게 나눕니다.',
  'intro-demo': '교사가 짧게 시연하고 무엇을 볼지 정합니다.',
  'learn-lecture': '핵심만 짧게 설명하고 질문으로 확인합니다.',
  'learn-inquiry': '자료를 찾아 기준에 맞춰 정리합니다.',
  'learn-discussion': '주장과 근거를 나누고 모둠 의견을 정리합니다.',
  'learn-gallery': '결과물을 돌며 보고, 메모로 피드백합니다.',
  'learn-pair': '배운 내용을 서로 설명하고 빈칸을 채웁니다.',
  'learn-individual': '스스로 풀거나 글로 정리합니다.',
  'learn-experiment': '절차에 따라 조작하고 결과를 기록합니다.',
  'learn-role': '상황을 맡아 재현하고 느낀 점을 나눕니다.',
  'learn-jigsaw': '전문가 모둠에서 익힌 뒤 본모둠에 설명합니다.',
  'learn-tps': '혼자 생각한 뒤 짝과 나누고 전체에 공유합니다.',
  'learn-station': '구석마다 다른 과제를 돌며 수행합니다.',
  'learn-make': '기준에 맞춰 결과물을 만듭니다.',
  'wrap-line': '오늘 배운 점을 한 문장으로 적습니다.',
  'wrap-exit': '확인 질문 하나에 답합니다.',
  'wrap-share': '결과물을 게시하고 한 가지 배운 점을 말합니다.',
  'wrap-check': '할 수 있는지 체크하고 더 필요한 점을 적습니다.',
  'wrap-next': '더 알고 싶은 점을 한 가지 적습니다.',
};

function withUpdatedSeedGuides(rows: LearningPlanActivity[]): LearningPlanActivity[] {
  const seedById = new Map(LEARNING_PLAN_ACTIVITY_SEED.map((row) => [row.id, row]));
  let changed = false;
  const next = rows.map((row) => {
    const seed = seedById.get(row.id);
    const previous = PREVIOUS_SEED_GUIDES[row.id];
    if (!seed || !previous || row.guide !== previous || row.guide === seed.guide) return row;
    changed = true;
    return { ...row, guide: seed.guide };
  });
  if (changed) saveLearningPlanActivities(next);
  return next;
}

/** 저장된 목록이 없으면 기본 활동 구조. 키를 지운 것과 한 번도 저장하지 않은 것을 구분한다. */
export function loadLearningPlanActivities(): LearningPlanActivity[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return LEARNING_PLAN_ACTIVITY_SEED.map((row) => ({ ...row }));
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return LEARNING_PLAN_ACTIVITY_SEED.map((row) => ({ ...row }));
    return withUpdatedSeedGuides(parsed.filter(isActivity).map((row) => ({ ...row })));
  } catch {
    return LEARNING_PLAN_ACTIVITY_SEED.map((row) => ({ ...row }));
  }
}

export function saveLearningPlanActivities(rows: LearningPlanActivity[]): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

export function resetLearningPlanActivities(): LearningPlanActivity[] {
  const next = LEARNING_PLAN_ACTIVITY_SEED.map((row) => ({ ...row }));
  saveLearningPlanActivities(next);
  saveLearningPlanCategories(defaultCategories());
  return next;
}
